import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import ReactGa from 'react-ga'
import { Box, Grid, makeStyles, Menu, MenuItem } from '@material-ui/core'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { NOOP } from '../utils'

import WorkIcon from '@material-ui/icons/Work'
import AccountBalanceIcon from '@material-ui/icons/AccountBalance'
import AssignmentIcon from '@material-ui/icons/Assignment'
import AccountCircleIcon from '@material-ui/icons/AccountCircle'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

const useStyles = makeStyles((theme) => ({
  jobContainer: {
    backgroundColor: `#FFFFFF`,
    marginBottom: `2.5rem`,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  listingPage: {
    padding: `0 6.125rem`,
  },
  jobTop: {
    padding: `2rem !important`,
    position: `relative`,
  },
  jobTitle: {
    display: 'inline-block',
    fontSize: `1.875rem`,
    lineHeight: `2.25rem`,
    margin: 0,
    [theme.breakpoints.up('md')]: {
      margin: `0 10rem 0 0`,
    },
  },
  jobDate: {
    color: `#707070`,
    fontStyle: `italic`,
    paddingTop: `0.5rem`,
    top: `0`,
    right: `2rem`,
    [theme.breakpoints.up('md')]: {
      paddingTop: 0,
      position: `absolute`,
      top: `3rem`,
    },
  },
  jobDescription: {
    color: `#707070`,
    maxWidth: `56.25rem`,
    margin: `0 0 3.625rem 0`,
    paddingTop: `2rem`,
  },
  jobFilterContainer: {
    display: `flex`,
    flexDirection: `column`,
    [theme.breakpoints.up('lg')]: {
      flexDirection: `row`,
    },
  },
  jobFilter: {
    display: `flex`,
    flexDirection: `column`,
    paddingRight: `0`,
    [theme.breakpoints.up('lg')]: {
      paddingRight: `3vw`,
    },
    '&:last-child': {
      paddingRight: `0`,
    },
  },
  jobFilterLabel: {
    display: `none`,
    margin: 0,
    textTransform: 'uppercase',
    [theme.breakpoints.up('lg')]: {
      display: `flex`,
    },
  },
  jobFilterFilter: {
    borderRadius: `0.1875rem`,
    color: `#1E2553`,
    display: `flex`,
    alignItems: `center`,
    padding: `0.5rem 2.5rem 0.5rem 0.75rem`,
  },
  jobFilterIconContainer: {
    alignItems: `center`,
    borderRadius: `50%`,
    display: `flex`,
    fontSize: `1rem`,
    height: `2rem`,
    justifyContent: `center`,
    marginRight: `0.5rem`,
    width: `2rem`,
  },
  jobFilterIcon: {
    fontSize: `1rem`,
  },
  jobFilterTypeIconContainer: {
    backgroundColor: `#E1EEF6`,
  },
  jobFilterType: {
    backgroundColor: `transparent`,
    [theme.breakpoints.up('lg')]: {
      backgroundColor: `#E1EEF6`,
      paddingRight: `1.5rem !important`,
    },
  },
  viewMoreIcon: {
    height: `1rem`,
  },
  jobFilterTypeMenu: {
    marginTop: `3rem !important`,
    minWidth: `9rem`,
  },
  jobFilterTypeMenuAnchor: {
    cursor: `pointer`,
  },
  jobFilterTypeMenuItem: {
    textTransform: `capitalize`,
    '&:hover': {
      backgroundColor: `transparent`,
      cursor: `text`,
    },
  },
  jobFilterDeptIconContainer: {
    backgroundColor: `#ECD3E5`,
  },
  jobFilterDept: {
    backgroundColor: `transparent`,
    [theme.breakpoints.up('lg')]: {
      backgroundColor: `#ECD3E5`,
    },
  },
  jobFilterTopicIconContainer: {
    backgroundColor: `#FDF2DF`,
  },
  jobFilterTopic: {
    backgroundColor: `transparent`,
    [theme.breakpoints.up('lg')]: {
      backgroundColor: `#FDF2DF`,
    },
  },
  jobFilterAttendingIconContainer: {
    backgroundColor: `#DBF0E0`,
  },
  jobFilterAttending: {
    backgroundColor: `transparent`,
    [theme.breakpoints.up('lg')]: {
      backgroundColor: `#DBF0E0`,
    },
  },
  jobBottom: {
    borderTop: `1px solid #EEE`,
    display: `flex`,
    justifyContent: `flex-end`,
    padding: `2rem`,
  },
  jobDetails: {
    color: `#FFFFFF`,
    cursor: `pointer`,
    backgroundColor: `#1E2553`,
    padding: `0.5625rem 2.5rem`,
    textAlign: `center`,
    textDecoration: `none`,
    width: `100%`,
    '&:hover': {
      textDecoration: `none`,
    },
    [theme.breakpoints.up('md')]: {
      width: `auto`,
    },
    borderRadius: '3px',
    boxShadow: '0 2px 2px #70707066',
  },
  newJob: {
    backgroundColor: theme.palette.secondary.main,
    borderRadius: 3,
    color: theme.palette.primary.light,
    fontSize: theme.typography.pxToRem(11),
    height: 22,
    marginTop: 0,
    position: 'relative',
    textAlign: 'center',
    verticalAlign: 'top',
    width: 48,
    [theme.breakpoints.up('sm')]: {
      display: 'inline-block',
      fontSize: theme.typography.pxToRem(18),
      height: 38,
      margin: `0 ${theme.spacing(2)}px 0 0`,
      width: 89,
    },
    '& > span': {
      left: '50%',
      position: 'absolute',
      textTransform: 'uppercase',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    },
  },
}))

// Considering anything <= 5 days old to be new.
const isNewListing = (creationDate) => dayjs().subtract(5, 'd').isSameOrBefore(dayjs(creationDate))

function JobListing({ job, listingPage }) {
  const classes = useStyles()

  const [anchorEl, setAnchorEl] = useState(null)
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const applicationAnalytics = () => {
    ReactGa.event({
      category: 'Active Project',
      action: 'Click Apply Now ',
    })
  }

  const detailsAnalytics = () => {
    ReactGa.event({
      category: "Active Project",
      action: "Click Details"
    })
  }

  return (
    <Grid container className={clsx(classes.jobContainer, listingPage && classes.listingPage)}>
      <Grid item xs={12} className={classes.jobTop}>
        {isNewListing(job.date) && (
          <h4 className={classes.newJob}>
            <span>New</span>
          </h4>
        )}
        <h3 className={classes.jobTitle}>{job.title}</h3>
        <Box className={classes.jobDate}>Created {dayjs(job.date).format('MM/DD/YYYY')}</Box>
        <p className={classes.jobDescription}>{job.description}</p>
        <Box className={classes.jobFilterContainer}>
          <Box className={classes.jobFilter}>
            <p className={classes.jobFilterLabel}>Position Type</p>
            <div
              className={clsx(
                classes.jobFilterFilter,
                classes.jobFilterType,
                job.type.length > 1 && classes.jobFilterTypeMenuAnchor
              )}
              onClick={job.type.length > 1 ? handleClick : NOOP}
            >
              <Box
                className={clsx(classes.jobFilterTypeIconContainer, classes.jobFilterIconContainer)}
              >
                <WorkIcon className={classes.jobFilterIcon} />
              </Box>
              {job.type.length > 1 ? (
                <span>
                  View All <ExpandMoreIcon className={classes.viewMoreIcon} />
                </span>
              ) : (
                  job.type
                )}
            </div>
            <Menu
              classes={{ paper: classes.jobFilterTypeMenu }}
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {job.type.map((type) => (
                <MenuItem className={classes.jobFilterTypeMenuItem} key={type}>
                  {type}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Box className={classes.jobFilter}>
            <p className={classes.jobFilterLabel}>Department</p>
            <div className={`${classes.jobFilterFilter} ${classes.jobFilterDept}`}>
              <Box
                className={clsx(classes.jobFilterDeptIconContainer, classes.jobFilterIconContainer)}
              >
                <AccountBalanceIcon className={classes.jobFilterIcon} />
              </Box>
              {job.dept}
            </div>
          </Box>
          <Box className={classes.jobFilter}>
            <p className={classes.jobFilterLabel}>Division</p>
            <div className={`${classes.jobFilterFilter} ${classes.jobFilterTopic}`}>
              <Box
                className={clsx(
                  classes.jobFilterTopicIconContainer,
                  classes.jobFilterIconContainer
                )}
              >
                <AssignmentIcon className={classes.jobFilterIcon} />
              </Box>
              {job.topic}
            </div>
          </Box>
          <Box className={classes.jobFilter}>
            <p className={classes.jobFilterLabel}>Project Lead</p>
            <div className={`${classes.jobFilterFilter} ${classes.jobFilterAttending}`}>
              <Box
                className={clsx(
                  classes.jobFilterAttendingIconContainer,
                  classes.jobFilterIconContainer
                )}
              >
                <AccountCircleIcon className={classes.jobFilterIcon} />
              </Box>
              {job.attending}
            </div>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} className={classes.jobBottom}>
        {listingPage ? (
          <a
            href={`mailto:${job.email}`}
            className={classes.jobDetails}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => applicationAnalytics()}
          >
            APPLY NOW
          </a>
        ) : (
            <Link
              to={`/listing/${job.id}`}
              className={classes.jobDetails}
              onClick={() => detailsAnalytics()}
            >
              DETAILS
            </Link>
          )}
      </Grid>
    </Grid>
  )
}

export default JobListing
